import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useResourceList, useResourceMutations } from "@/hooks/use-admin-resource";
import { AdminPageHeader, RowActions, ResponsiveTableWrap, AdminEmptyState, slugify } from "@/components/admin/crud-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MediaField } from "@/components/admin/media-picker";
import { AiAssistButton } from "@/components/ai/ai-assist-button";

export const Route = createFileRoute("/_authenticated/admin/pages")({
  component: PagesAdmin,
});

type Page = {
  id: string; title: string; slug: string; blocks: any;
  cover_url: string | null;
  seo_title: string | null; seo_description: string | null;
  published: boolean; updated_at: string;
};

function PagesAdmin() {
  const { data: pages, isLoading } = useResourceList<Page>("pages", "updated_at");
  const { create, update, remove } = useResourceMutations("pages");
  const [editing, setEditing] = useState<Page | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "published" | "draft">("all");

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (p: Page) => { setEditing(p); setOpen(true); };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (pages ?? []).filter((p) => {
      if (filter === "published" && !p.published) return false;
      if (filter === "draft" && p.published) return false;
      if (!q) return true;
      return p.title.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q);
    });
  }, [pages, search, filter]);

  const tabs: { id: typeof filter; label: string }[] = [
    { id: "all", label: `All (${pages?.length ?? 0})` },
    { id: "published", label: `Live (${pages?.filter((p) => p.published).length ?? 0})` },
    { id: "draft", label: `Drafts (${pages?.filter((p) => !p.published).length ?? 0})` },
  ];

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Pages"
        description="Build pages from blocks. Publish when ready."
        onNew={openNew}
        newLabel="New page"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search title or slug…"
        count={rows.length}
      />

      <div className="flex flex-wrap gap-1.5">
        {tabs.map((t) => (
          <Button key={t.id} size="sm" variant={filter === t.id ? "default" : "outline"} onClick={() => setFilter(t.id)}>
            {t.label}
          </Button>
        ))}
      </div>

      {!isLoading && !rows.length ? (
        <AdminEmptyState
          title={search || filter !== "all" ? "No pages match" : "No pages yet"}
          action={<Button size="sm" onClick={openNew}>Create your first page</Button>}
        />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <ResponsiveTableWrap>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden sm:table-cell">Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
                )}
                {rows.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">
                      <div className="truncate">{p.title}</div>
                      <div className="text-xs text-muted-foreground sm:hidden">/{p.slug}</div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-muted-foreground">/{p.slug}</TableCell>
                    <TableCell>
                      <Badge variant={p.published ? "default" : "secondary"}>
                        {p.published ? "Published" : "Draft"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActions
                        onEdit={() => openEdit(p)}
                        onDelete={() => { if (confirm(`Delete "${p.title}"?`)) remove.mutate(p.id); }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ResponsiveTableWrap>
        </div>
      )}

      <PageDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        onSubmit={(values) => {
          if (editing) update.mutate({ id: editing.id, values }, { onSuccess: () => setOpen(false) });
          else create.mutate(values, { onSuccess: () => setOpen(false) });
        }}
      />
    </div>
  );
}

function PageDialog({
  open, onOpenChange, editing, onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: Page | null;
  onSubmit: (values: any) => void;
}) {
  const [title, setTitle] = useState(editing?.title ?? "");
  const [slug, setSlug] = useState(editing?.slug ?? "");
  const [blocks, setBlocks] = useState(JSON.stringify(editing?.blocks ?? [], null, 2));
  const [cover, setCover] = useState(editing?.cover_url ?? "");
  const [seoTitle, setSeoTitle] = useState(editing?.seo_title ?? "");
  const [seoDesc, setSeoDesc] = useState(editing?.seo_description ?? "");
  const [published, setPublished] = useState(editing?.published ?? false);

  // reset when editing changes
  useState(() => {
    setTitle(editing?.title ?? "");
    setSlug(editing?.slug ?? "");
    setBlocks(JSON.stringify(editing?.blocks ?? [], null, 2));
    setCover(editing?.cover_url ?? "");
    setSeoTitle(editing?.seo_title ?? "");
    setSeoDesc(editing?.seo_description ?? "");
    setPublished(editing?.published ?? false);
    return null;
  });

  const handleSubmit = () => {
    let parsedBlocks: any = [];
    try { parsedBlocks = JSON.parse(blocks || "[]"); }
    catch { alert("Blocks must be valid JSON"); return; }
    onSubmit({
      title, slug: slug || slugify(title), blocks: parsedBlocks,
      cover_url: cover || null,
      seo_title: seoTitle || null, seo_description: seoDesc || null, published,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{editing ? "Edit page" : "New page"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder={slugify(title)} />
          </div>
          <div className="grid gap-2">
            <Label>Blocks (JSON)</Label>
            <Textarea rows={6} value={blocks} onChange={(e) => setBlocks(e.target.value)} className="font-mono text-xs" />
            <p className="text-xs text-muted-foreground">Array of block objects, e.g. <code>{`[{"type":"hero","title":"..."}]`}</code></p>
          </div>
          <MediaField label="Cover image" value={cover} onChange={(url) => setCover(url ?? "")} />
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label>SEO title</Label>
              <Input value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>SEO description</Label>
              <Input value={seoDesc} onChange={(e) => setSeoDesc(e.target.value)} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <AiAssistButton
              task="page_copy"
              context={blocks}
              defaultPrompt={title ? `Write marketing copy blocks for the "${title}" page.` : "Write marketing copy for this page."}
              label="AI page copy"
              onApply={(text) => setBlocks(text)}
            />
            <AiAssistButton
              task="seo_meta"
              context={`Title: ${title}\n\nBlocks: ${blocks}`}
              defaultPrompt="Generate SEO title and description for this page."
              label="Auto-fill SEO meta"
              onApply={(text) => {
                try {
                  const clean = text.replace(/```json|```/g, "").trim();
                  const json = JSON.parse(clean);
                  if (json.title) setSeoTitle(String(json.title).slice(0, 60));
                  if (json.description) setSeoDesc(String(json.description).slice(0, 160));
                } catch { /* noop */ }
              }}
            />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={published} onCheckedChange={setPublished} />
            <Label>Published</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
